import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import LocationCocSelector from "@/components/Forms/components/Selectors/LocationCocSelector";
import { Signature, SignatureField } from "@/Features";
import { useForm, zodResolver } from "@mantine/form";
import {
  Button,
  DateInput,
  Grid,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useRef, useState } from "react";
import z from "zod";

export type CocIntakeFormValues = {
  custody_to: { location_id: number; location_name: string };
  custody_to_sig: Signature;
  custody_from: string;
  custody_from_sig: Signature;
  timestamp: string;
  location_received: string;
  reason: string;
};

export type CocIntakeFormProps = {
  submitting?: boolean;
  onCancel?: () => void;
  onSubmit?: (d: CocIntakeFormValues) => void;
  onChange?: (values: CocIntakeFormValues) => void;
  showButtonMenu?: boolean;
  defaultFormData?: CocIntakeFormValues;
  disabled?: boolean;
};

export const CocIntakeFormSchema = z.object({
  timestamp: z
    .string({ message: "Timestamp is required" })
    .min(1, { message: "Timestamp is required" }),
  custody_from: z
    .string({ message: "Received from is required" })
    .min(1, { message: "Received from is required" }),
  custody_from_sig: z.any(),
  custody_to_sig: z.any(),
  custody_to: z
    .object({
      location_id: z
        .number({ message: "Received by is required" })
        .min(1, { message: "Received by is required" }),
    })
    .catchall(z.any()),
  location_received: z.string().optional(),
  reason: z.string().optional(),
});

export const CocIntakeForm: React.FC<CocIntakeFormProps> = ({
  submitting,
  onCancel,
  onSubmit,
  onChange,
  showButtonMenu = true,
  disabled = false,
}) => {
  const [submittingState, setSubmittingState] = useState(false);

  const isSubmitControlled = useRef(submitting !== undefined);

  const _submitting = isSubmitControlled.current ? submitting : submittingState;

  const form = useForm<CocIntakeFormValues>({
    validate: zodResolver(CocIntakeFormSchema),
    onValuesChange: (values) => {
      onChange?.(values);
    },
  });

  const formData = form.getValues();

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const handleSubmit = () => {
    setSubmittingState(true);
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      setSubmittingState(false);
      return;
    }

    onSubmit?.(formData);
    setSubmittingState(false);
  };

  return (
    <FormWrapper style={{ minWidth: 550, maxWidth: 550 }}>
      <Grid col={2}>
        <LocationCocSelector
          label="Received By"
          description={"Person or location receiving item"}
          placeholder="Select location"
          required={true}
          disabled={disabled || _submitting}
          key={form.key("custody_to")}
          {...form.getInputProps("custody_to")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Received From"
          placeholder={"John Smith, FedEx: tracking 12345..."}
          description={
            "Person, location, or method from which the item was received"
          }
          required={true}
          disabled={disabled || _submitting}
          key={form.key("custody_from")}
          {...form.getInputProps("custody_from")}
        />

        <div>
          <SignatureField
            label="Received By Signature"
            useESignature={true}
            disabled={disabled || _submitting}
            signer={formData?.custody_to?.location_name}
            {...form.getInputProps("custody_to_sig")}
            key={form.key("custody_to_sig")}
          />
        </div>

        <div>
          <SignatureField
            label="Received From Signature"
            disabled={disabled || _submitting}
            signer={formData?.custody_from}
            {...form.getInputProps("custody_from_sig")}
            key={form.key("custody_from_sig")}
          />
        </div>

        <DateInput
          size="sm"
          variant="outlined"
          label="Intake Timestamp"
          description={"Date and time the item was received"}
          enableCalendar
          arrow
          clearable={true}
          disabled={disabled || _submitting}
          format="YYYY-MM-DD HH:mm:ss"
          required={true}
          key={form.key("timestamp")}
          {...form.getInputProps("timestamp")}
        />

        <TextInput
          size="sm"
          variant="outlined"
          label="Location Received"
          placeholder="Address, Front Office, etc..."
          key={form.key("location_received")}
          disabled={disabled || _submitting}
          {...form.getInputProps("location_received")}
          description={
            "Person or location where item is currently being stored"
          }
        />

        <div style={{ gridColumn: "1 / -1" }}>
          <TextAreaInput
            size="sm"
            variant="outlined"
            label="Notes"
            placeholder={"Enter any additional notes on this item"}
            description={"Any additional notes on this item"}
            minRows={4}
            key={form.key("reason")}
            disabled={disabled || _submitting}
            {...form.getInputProps("reason")}
          />
        </div>
      </Grid>
      {showButtonMenu && (
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={_submitting || disabled}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={_submitting || disabled}
            loading={_submitting}
          >
            Submit
          </Button>
        </ButtonMenu>
      )}
    </FormWrapper>
  );
};
